import { useRef, useState, type KeyboardEvent } from "react";
import type { Task } from "@common/types/task";
import { TextArea } from "@design-system/TextArea/TextArea";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { useViewOnlyRefusal } from "@hooks/permissions/useViewOnlyRefusal";
import { isWindowSwitchBlur, LINE_BREAKS } from "../board/TaskTitleEditor";
import styles from "./TaskDetail.module.scss";

interface TaskDetailTitleProps {
    task: Task;
    onRename: (body: { title: string }) => Promise<Task>;
}

/**
 * The Details section's title field, growing to fit long titles. Enter or
 * leaving the field saves the trimmed title (empty or unchanged saves
 * nothing); switching tabs or apps keeps the edit; Escape cancels. A failed
 * save toasts and puts the title back, unless the user is editing it again.
 * View-only users get the plain title.
 */
export const TaskDetailTitle = ({
    task,
    onRename,
}: Readonly<TaskDetailTitleProps>) => {
    const { isViewOnly } = useViewOnlyRefusal();
    const { error: errorToast } = useToaster();
    const [draft, setDraft] = useState(task.title);
    const [syncedTitle, setSyncedTitle] = useState(task.title);
    const [isFocused, setIsFocused] = useState(false);
    const isCanceledRef = useRef(false);
    const fieldRef = useRef<HTMLTextAreaElement>(null);

    if (!isFocused && task.title !== syncedTitle) {
        setSyncedTitle(task.title);
        setDraft(task.title);
    }

    if (isViewOnly) {
        return <p className={styles.detailValue}>{task.title}</p>;
    }

    const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
        if (
            (event.key === "Enter" || event.key === "Escape") &&
            !event.nativeEvent.isComposing
        ) {
            event.preventDefault();
            isCanceledRef.current = event.key === "Escape";
            event.currentTarget.blur();
        }
    };

    const handleBlur = () => {
        if (isWindowSwitchBlur(fieldRef.current)) {
            return;
        }
        setIsFocused(false);
        const nextTitle = draft.trim();
        if (
            isCanceledRef.current ||
            nextTitle === "" ||
            nextTitle === task.title
        ) {
            isCanceledRef.current = false;
            setDraft(task.title);
            return;
        }
        const previousTitle = task.title;
        setDraft(nextTitle);
        onRename({ title: nextTitle }).catch(() => {
            errorToast("Failed to rename task");
            if (document.activeElement !== fieldRef.current) {
                setDraft(previousTitle);
            }
        });
    };

    return (
        <TextArea
            ref={fieldRef}
            autoGrow
            minRows={1}
            aria-label="Title"
            value={draft}
            onValueChange={(value) => setDraft(value.replace(LINE_BREAKS, " "))}
            onFocus={() => setIsFocused(true)}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            className={styles.titleField}
        />
    );
};
